import { useState } from 'react';
import { ArrowRight, Check } from '@ui/shared/icons';
import { formatShortDate } from '@ui/shared/format/date';

/**
 * Byte-for-byte reuse of the `AdjustmentOfferCard` composition already
 * shipped on Dashboard (`DashboardScreen.desktop.tsx`) and Objective Detail
 * — extracted here so the new desktop create/edit screen can reuse the exact
 * same visual anatomy without a second, drifting copy (design/screens/
 * objective-create-desktop.md SS3/SS7.5/SS8.4: "no new icon, no sparkle,
 * same Apply/arrow construction"). Adds an explicit "Dismiss" text action
 * beside Apply per SS7.5's own addition for a drafting context — the
 * Dashboard/Detail versions have no visible Dismiss, this one does.
 */
export function AdjustmentOfferCard({
  title,
  detail,
  suggestedTargetDate,
  onApply,
  onDismiss,
}: {
  readonly title: string;
  readonly detail: string;
  readonly suggestedTargetDate?: string;
  readonly onApply: () => Promise<void> | void;
  readonly onDismiss?: () => void;
}) {
  const [applied, setApplied] = useState(false);

  if (applied) {
    return (
      <div style={{ marginTop: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-primary)' }}>
        <Check size={14} color="var(--hue-secondary)" aria-hidden="true" />
        {suggestedTargetDate ? `Target moved to ${formatShortDate(suggestedTargetDate)}.` : 'Applied.'}
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 'var(--space-3)',
        background: 'var(--surface-hover)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-2)',
      }}
    >
      <div>
        <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--text-primary)' }}>{title}</div>
        <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-1)' }}>{detail}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flex: 'none' }}>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', minHeight: 'var(--touch-target-min)' }}
          >
            Dismiss
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            void Promise.resolve(onApply()).then(() => setApplied(true));
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
            color: 'var(--hue-primary)',
            minHeight: 'var(--touch-target-min)',
            whiteSpace: 'nowrap',
          }}
        >
          Apply <ArrowRight size={14} color="var(--hue-primary-vivid)" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
